import { Amount, ProviderCollectionInvoice, Variables, escapeHtml, renderAmount, renderDate } from '@enter/core';

const money = (value?: Amount): string => renderAmount(value) || '$0.00';

const dateOrDash = (value?: string): string => renderDate(value) || '—';

/**
 * Self-contained HTML for the collections invoice statement print/PDF overlay.
 * White paper only — no card border (preview chrome lives on the overlay).
 */
export const buildInvoiceStatementPrintHtml = (
    invoice: ProviderCollectionInvoice,
    options?: { patientPageHref?: string },
): string => {
    const info = invoice.invoicePatient?.info;
    const patientLabel =
        [info?.patientFirstName, info?.patientLastName].filter(Boolean).join(' ') || '—';
    const externalPatientId = invoice.invoicePatient?.externalPatientId || '—';
    const facilityLabel = invoice.facilityName || 'Billing provider';
    const renderingProvider =
        [invoice.renderingProviderFirstName, invoice.renderingProviderLastName]
            .filter(Boolean)
            .join(' ') || '—';
    const address =
        [info?.patientAddress1, info?.patientCity, info?.patientState, info?.patientZip]
            .filter(Boolean)
            .join(', ') || '—';
    const dob = info?.patientDateOfBirth ? dateOrDash(info.patientDateOfBirth) : '';
    const patientMeta = `Patient ID ${escapeHtml(externalPatientId)}${
        dob ? ` · DOB ${escapeHtml(dob)}` : ''
    }`;
    const howToPayAccount =
        externalPatientId !== '—'
            ? ` and reference account <span class="accent">${escapeHtml(externalPatientId)}</span>`
            : '';
    const questions =
        invoice.memoNotes?.trim() ||
        `Contact ${facilityLabel} with questions about this statement.`;

    const lines = (invoice.invoiceItems || [])
        .map(
            li => `
      <tr>
        <td>${escapeHtml(li.procedureCptDescription || '—')}</td>
        <td class="code">${escapeHtml(li.procedureCpt || '—')}</td>
        <td class="num">${escapeHtml(money(li.procedureCharge))}</td>
        <td class="num">${escapeHtml(money(li.payerPayments))}</td>
        <td class="num strong">${escapeHtml(money(li.patientResponsibility))}</td>
      </tr>`,
        )
        .join('');

    const emptyLines = !lines
        ? `<tr><td colspan="5" class="muted">No line items on this invoice.</td></tr>`
        : lines;

    const enterLink = options?.patientPageHref
        ? `<a class="enter-link" href="${escapeHtml(options.patientPageHref)}" target="_blank" rel="noopener noreferrer">Open Enter Patient Page</a>`
        : '';

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<style>
  :root {
    --text-strong: ${Variables.ThemeLight.TextStrong};
    --text-medium: ${Variables.ThemeLight.TextMedium};
    --text-mild: ${Variables.ThemeLight.TextMild};
    --border: ${Variables.ThemeLight.Border1};
    --bg: ${Variables.ThemeLight.Background3};
    --bg-muted: ${Variables.ThemeLight.Background1};
    --primary: ${Variables.ThemeLight.PrimaryConstant6};
    --primary-fg: ${Variables.ThemeLight.PrimaryInverted3};
    --primary-border: ${Variables.ThemeLight.PrimaryMild1};
    --primary-bg: ${Variables.ThemeLight.PrimaryMild2};
  }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: transparent;
    font-family: Arial, Helvetica, sans-serif;
    color: var(--text-strong);
  }
  .paper {
    width: 816px;
    max-width: 100%;
    min-height: 11in;
    margin: 0 auto;
    background: var(--bg);
    display: flex;
    flex-direction: column;
    color: var(--text-strong);
  }
  .header {
    padding: 34px 40px 26px;
    border-bottom: 1px solid var(--border);
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
  }
  .brand {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: var(--primary);
    margin-bottom: 12px;
  }
  .provider { font-size: 15px; font-weight: 600; }
  .muted { font-size: 13px; line-height: 1.6; color: var(--text-mild); }
  .title {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.1;
    text-align: right;
  }
  .meta {
    margin-top: 16px;
    display: grid;
    grid-template-columns: auto auto;
    gap: 4px 16px;
    font-size: 13px;
    color: var(--text-mild);
    text-align: right;
  }
  .meta strong {
    color: var(--text-strong);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .two-col {
    padding: 24px 40px;
    display: flex;
    gap: 40px;
    border-bottom: 1px solid var(--border);
  }
  .two-col > div { flex: 1; min-width: 0; }
  .eyebrow {
    font-size: 10px;
    font-weight: 600;
    color: var(--text-mild);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 9px;
  }
  .patient-name { font-size: 16px; font-weight: 600; }
  .visit-grid {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 5px 14px;
    font-size: 13px;
    line-height: 1.4;
    color: var(--text-mild);
    text-align: left;
  }
  .visit-grid strong {
    color: var(--text-strong);
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }
  .enter-link {
    margin-top: 11px;
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: 500;
    color: var(--primary-fg);
    border: 1px solid var(--primary-border);
    background: var(--primary-bg);
    border-radius: 6px;
    padding: 7px 11px;
    text-decoration: none;
  }
  table {
    width: 100%;
    border-collapse: collapse;
  }
  th, td {
    padding: 12px 40px;
    border-bottom: 1px solid var(--bg-muted);
    font-size: 14px;
    text-align: left;
  }
  th {
    padding-top: 14px;
    padding-bottom: 9px;
    font-size: 10px;
    font-weight: 600;
    color: var(--text-mild);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid var(--border);
  }
  td.code { color: var(--text-mild); font-variant-numeric: tabular-nums; }
  td.num, th.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  td.strong { font-weight: 600; }
  .totals {
    padding: 18px 40px 26px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  .total-row {
    display: flex;
    justify-content: space-between;
    width: 320px;
    max-width: 100%;
    padding: 7px 0;
    font-size: 13px;
    color: var(--text-medium);
    font-variant-numeric: tabular-nums;
  }
  .due {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    width: 320px;
    max-width: 100%;
    padding: 14px;
    margin-top: 12px;
    background: var(--primary-bg);
    border: 1px solid var(--primary-border);
    border-radius: 9px;
  }
  .due span { font-size: 14px; font-weight: 600; }
  .due strong {
    font-size: 24px;
    font-weight: 700;
    color: var(--primary);
    font-variant-numeric: tabular-nums;
  }
  .footer {
    margin-top: auto;
    padding: 22px 40px;
    border-top: 1px solid var(--border);
    background: var(--bg-muted);
    display: flex;
    gap: 40px;
  }
  .footer > div { flex: 1; }
  .footer-eyebrow {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-medium);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 9px;
  }
  .footer-body {
    font-size: 13px;
    line-height: 1.65;
    color: var(--text-mild);
  }
  .accent { color: var(--primary-fg); font-weight: 500; }
  @page { size: letter; margin: 0.5in; }
  @media print {
    .paper { min-height: 10in; }
    .enter-link { display: none; }
  }
</style>
</head>
<body>
  <div class="paper ps-paper">
    <div class="header">
      <div>
        <div class="brand">ENTER</div>
        <div class="provider">${escapeHtml(facilityLabel)}</div>
        <div class="muted">${escapeHtml(renderingProvider === '—' ? '' : renderingProvider)}</div>
      </div>
      <div>
        <div class="title">Statement of<br/>Patient Responsibility</div>
        <div class="meta">
          <span>Statement #</span><strong>${escapeHtml(invoice.controlNumber || '—')}</strong>
          <span>Statement date</span><strong>${escapeHtml(
              dateOrDash(invoice.postDate || invoice.approveDate),
          )}</strong>
          <span>Account</span><strong>${escapeHtml(externalPatientId)}</strong>
        </div>
      </div>
    </div>

    <div class="two-col">
      <div>
        <div class="eyebrow">Billed to</div>
        <div class="patient-name">${escapeHtml(patientLabel)}</div>
        <div class="muted">${escapeHtml(address)}<br/>${patientMeta}</div>
        ${enterLink}
      </div>
      <div>
        <div class="eyebrow">Visit summary</div>
        <div class="visit-grid">
          <span>Date of service</span><strong>${escapeHtml(dateOrDash(invoice.serviceDate))}</strong>
          <span>Rendering provider</span><strong>${escapeHtml(renderingProvider)}</strong>
          <span>Insurance</span><strong>${escapeHtml(invoice.primaryPayerName || '—')}</strong>
        </div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th>Code</th>
          <th class="num">Charge</th>
          <th class="num">Insurance</th>
          <th class="num">Your resp.</th>
        </tr>
      </thead>
      <tbody>
        ${emptyLines}
      </tbody>
    </table>

    <div class="totals">
      <div class="total-row"><span>Total charges</span><span>${escapeHtml(money(invoice.invoiceAmount))}</span></div>
      <div class="total-row"><span>Patient payments</span><span>−${escapeHtml(money(invoice.patientPayments))}</span></div>
      <div class="total-row"><span>Discounts</span><span>−${escapeHtml(money(invoice.patientDiscounts))}</span></div>
      <div class="due"><span>Amount due</span><strong>${escapeHtml(money(invoice.currentBalance))}</strong></div>
    </div>

    <div class="footer ps-footer">
      <div>
        <div class="footer-eyebrow">How to pay</div>
        <div class="footer-body">
          Contact <span class="accent">${escapeHtml(facilityLabel)}</span>${howToPayAccount}.
          Checks payable to ${escapeHtml(facilityLabel)}.
        </div>
      </div>
      <div>
        <div class="footer-eyebrow">Questions about this statement?</div>
        <div class="footer-body">${escapeHtml(questions)}</div>
      </div>
    </div>
  </div>
</body>
</html>`;
};
